import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread]               = useState(0);

  const load = useCallback(() => {
    if (!user) return;
    api.get('/notifications', { params: { limit: 20 } })
      .then(r => {
        const data = r.data?.data || [];
        setNotifications(data);
        // Count unread notifications (those not read by current user)
        setUnread(data.filter(n => !n.read).length);
      })
      .catch(() => {});
  }, [user]);

  // Add method to manually trigger refresh
  const refresh = useCallback(() => {
    load();
  }, [load]);

  // Poll every 30 seconds
  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const markRead = async (id) => {
    await api.put(`/notifications/${id}/read`).catch(() => {});
    setNotifications(prev => prev.map(n => n._id === id ? { ...n, read: true } : n));
    setUnread(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    await api.put('/notifications/read-all').catch(() => {});
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnread(0);
  };

  const deleteNotification = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      // Remove from local state immediately for better UX
      setNotifications(prev => prev.filter(n => n._id !== id));
      setUnread(prev => {
        const deletedNotif = notifications.find(n => n._id === id);
        return deletedNotif && !deletedNotif.read ? Math.max(0, prev - 1) : prev;
      });
    } catch (error) {
      console.error('Delete notification error:', error);
      throw error;
    }
  };

  const deleteAllNotifications = async () => {
    try {
      await api.delete('/notifications/delete-all/all');
      // Clear local state immediately for better UX
      setNotifications([]);
      setUnread(0);
    } catch (error) {
      console.error('Delete all notifications error:', error);
      throw error;
    }
  };

  return (
    <NotificationContext.Provider value={{ notifications, unread, load, refresh, markRead, markAllRead, deleteNotification, deleteAllNotifications }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);
